import { faCircle, faEnvelope, faLocationPin, faPencil, faPhone, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components";
import { COMMUNICATION_CHANNELS, COMMUNICATION_EVENTS } from "models";
import { formatDate } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { CommunicationEvent } from "models";

type CommunicationEventsProps = {
  events: CommunicationEvent[];
};

const getCommunicationEventName = (event: CommunicationEvent, t: TFunction) => {
  switch (event.type) {
    case COMMUNICATION_EVENTS.OnlineCheckInSent:
    case COMMUNICATION_EVENTS.DiagnoseOverviewSent:
    case COMMUNICATION_EVENTS.RepairOverviewSent:
      return t("v8_sent", "Sent");

    case COMMUNICATION_EVENTS.OnlineCheckInOpened:
    case COMMUNICATION_EVENTS.DiagnoseOverviewOpened:
    case COMMUNICATION_EVENTS.RepairOverviewOpened:
      return t("v8_opened", "Opened");

    case COMMUNICATION_EVENTS.DiagnoseOverviewResent:
    case COMMUNICATION_EVENTS.RepairOverviewResent:
      return t("v8_resent", "Resent");

    case COMMUNICATION_EVENTS.OnlineCheckInAnswered:
    case COMMUNICATION_EVENTS.DiagnoseOverviewAnswered:
      return t("v8_answered", "Answered");

    default:
      return "";
  }
};

export const CommunicationEvents = ({ events }: CommunicationEventsProps) => {
  const { t } = useTranslation();

  if (!events.length) return null;

  return (
    <div className="info-section">
      <div className="section-header">{t("v8_communication_events", "Communication events")}</div>
      <div className="section-content">
        <Grid>
          {events.map((event, key) => {
            const fullname = event.customer && `${event.customer.title} ${event.customer.firstname} ${event.customer.surname}`;

            return (
              <Grid.Row key={key}>
                <Grid.Column width={12}>{getCommunicationEventName(event, t)}</Grid.Column>

                <Grid.Column width={4} className="event-timestamp">
                  {formatDate(event.created_on, DATE_FORMATS_FNS.hoursMinutes)} - {formatDate(event.created_on, DATE_FORMATS_FNS.dateMonthYear)}
                </Grid.Column>

                <Grid.Row width={16} className="section-rows">
                  {event.receivers?.map((receiver, i) => (
                    <React.Fragment key={i}>
                      <span>
                        <FontAwesomeIcon icon={receiver.channel === COMMUNICATION_CHANNELS.Email ? faEnvelope : faPhone} className="i-green mr-5" />
                        {receiver.destination}
                      </span>
                      {receiver.reason && (
                        <span className="ml-10">
                          <FontAwesomeIcon key={i} icon={faCircle} color="red" className="mr-5" />
                          {receiver.reason}
                        </span>
                      )}
                    </React.Fragment>
                  ))}

                  {fullname && (
                    <span>
                      <FontAwesomeIcon icon={faUser} className="i-green mr-5" />
                      {fullname}
                    </span>
                  )}

                  {event.customer_name && (
                    <span>
                      <FontAwesomeIcon icon={faPencil} className="i-green mr-5" />
                      {event.customer_name}
                    </span>
                  )}

                  {event.ip && (
                    <>
                      <span className="icon-text">
                        IP
                        <FontAwesomeIcon icon={faLocationPin} className="i-green mr-5 ml-5" />
                      </span>
                      {event.ip}
                    </>
                  )}

                  {event.key_locker_pin_sent && event.type === COMMUNICATION_EVENTS.OnlineCheckInAnswered && <span>{t("v8_pin_sent", "Pin sent")}</span>}
                  {event.is_parking_gate_code_sent && <span>{t("v8_parking_gate_code_sent", "Parking gate code sent")}</span>}
                </Grid.Row>
              </Grid.Row>
            );
          })}
        </Grid>
      </div>
    </div>
  );
};
